import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import type { PtoRequest } from "@/types/db";
import { fmtDay } from "@/lib/utils";
import { Field, SelectField, TextareaField } from "@/components/Field";

const REASONS: PtoRequest["reason"][] = ["vacation", "sick", "personal", "bereavement", "jury_duty", "other"];

function reasonLabel(r: PtoRequest["reason"]): string {
  return { vacation: "Vacation", sick: "Sick", personal: "Personal", bereavement: "Bereavement", jury_duty: "Jury duty", other: "Other" }[r];
}

export function TimeOff() {
  const { employee } = useAuth();
  const [rows, setRows] = useState<PtoRequest[]>([]);
  const [showNew, setShowNew] = useState(false);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [reason, setReason] = useState<PtoRequest["reason"]>("vacation");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { if (employee) void load(); }, [employee?.id]);
  async function load() {
    if (!employee) return;
    const { data } = await supabase.from("hq_pto_requests").select("*")
      .eq("employee_id", employee.id)
      .order("start_date", { ascending: false });
    setRows((data as PtoRequest[]) ?? []);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!employee) return;
    setBusy(true); setError(null);
    const { error } = await supabase.from("hq_pto_requests").insert({
      employee_id: employee.id,
      start_date: start, end_date: end, reason, notes: notes || null,
    });
    setBusy(false);
    if (error) setError(error.message);
    else { setShowNew(false); setStart(""); setEnd(""); setNotes(""); await load(); }
  }

  async function cancel(r: PtoRequest) {
    const isApproved = r.request_status === "approved";
    const msg = isApproved
      ? `Cancel your already-approved time off (${r.start_date} → ${r.end_date})? Your manager will be notified so they can adjust the schedule.`
      : "Cancel this time off request?";
    if (!confirm(msg)) return;
    const { error: err } = await supabase
      .from("hq_pto_requests")
      .update({ request_status: "cancelled" })
      .eq("id", r.id);
    if (err) { setError(err.message); return; }
    await load();
  }

  const upcoming = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return rows.filter(r => r.request_status === "approved" && r.end_date >= today);
  }, [rows]);

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Time Off</h1>
        <button onClick={() => setShowNew(s => !s)} className="btn">+ Request Time Off</button>
      </div>

      {upcoming.length > 0 && (
        <div className="card bg-ok-bg ring-ok">
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-ok">Upcoming approved time off</h2>
          <div className="space-y-1">
            {upcoming.map(u => (
              <div key={u.id} className="text-sm">
                <span className="font-semibold">{fmtDay(u.start_date)} – {fmtDay(u.end_date)}</span>
                {" · "}<span className="text-gray2">{reasonLabel(u.reason)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {showNew && (
        <div className="card">
          <div className="mb-3 rounded-lg bg-offwhite px-3.5 py-2.5 text-xs text-gray2">
            Heads up — time off is currently <strong>unpaid</strong>. Submit your dates and your manager will approve or deny.
          </div>
          {error && <div className="mb-3 rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">{error}</div>}
          <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
            {/* A11Y-001: these labels were siblings with no htmlFor, so the
                accessibility tree showed two unnamed date fields. */}
            <Field label="Start date" type="date" required value={start}
              onChange={e => setStart(e.target.value)} />
            <Field label="End date" type="date" required min={start} value={end}
              onChange={e => setEnd(e.target.value)} />
            <div>
              <SelectField label="Reason" value={reason}
                onChange={e => setReason(e.target.value as PtoRequest["reason"])}>
                {REASONS.map(r => <option key={r} value={r}>{reasonLabel(r)}</option>)}
              </SelectField>
              {reason === "sick" && <p className="mt-1 text-xs text-warn">Calling in sick? Please also message your manager as early as you can — <b>at least 2 hours before your shift</b> — so we can cover.</p>}
            </div>
            <div className="sm:col-span-2">
              <TextareaField label="Notes" value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Any context for your manager…" />
            </div>
            <div className="sm:col-span-2 flex justify-end gap-2">
              <button type="button" className="btn btn-ghost" onClick={() => setShowNew(false)}>Cancel</button>
              <button className="btn" disabled={busy}>{busy ? "Submitting…" : "Submit request"}</button>
            </div>
          </form>
        </div>
      )}

      <div className="card">
        <h2 className="mb-3 text-base font-semibold">My requests</h2>
        {rows.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray2">No requests yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left text-xs uppercase tracking-wider text-gray2">
                <th className="py-2">Dates</th><th>Reason</th><th>Status</th><th>Notes</th><th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-b border-line/70">
                  <td className="py-2">{fmtDay(r.start_date)} – {fmtDay(r.end_date)}</td>
                  <td className="py-2 text-gray2">{reasonLabel(r.reason)}</td>
                  <td className="py-2">
                    <span className={`pill ${
                      r.request_status === "approved" ? "pill-ok" :
                      r.request_status === "denied" ? "pill-bad" :
                      r.request_status === "cancelled" ? "" : "pill-warn"
                    }`}>{r.request_status}</span>
                  </td>
                  <td className="py-2 text-gray2 max-w-xs">
                    {r.notes && <div className="truncate">{r.notes}</div>}
                    {r.denial_reason && <div className="text-bad text-xs">Denied: {r.denial_reason}</div>}
                  </td>
                  <td className="py-2 text-right">
                    {(r.request_status === "pending"
                      || (r.request_status === "approved" && r.start_date >= new Date().toISOString().slice(0, 10))
                    ) && (
                      <button onClick={() => cancel(r)} className="btn btn-sm btn-ghost">Cancel</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
